import { Currency } from "@redotech/money/currencies";
import { composeWrappers } from "@redotech/react-util/component";
import { CurrencyConverterProvider } from "@redotech/react-util/money";
import { useBreadcrumbs } from "@redotech/redo-web/breadcrumb";
import { NotFoundPage } from "@redotech/redo-web/login/not-found-page";
import { NotificationProvider } from "@redotech/redo-web/notification-provider";
import { CustomSnackbarProvider } from "@redotech/redo-web/snackbar";
import { ThemeProvider } from "@redotech/redo-web/theme-provider";
import { ThursdayBootWarningBanner } from "@redotech/redo-web/thursday-boot-warning-banner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { memo, useContext } from "react";
import { createRoot } from "react-dom/client";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useMatch,
} from "react-router-dom";
import { useDocumentTitle } from "usehooks-ts";
import { aeoDashboardRoutes } from "./aeo-dashboard/route";
import { agentRoutes } from "./agents/navigation/route";
import { RedoAdminAgentsRpcClientProvider } from "./agents/providers/redo-admin-agents-rpc-client-provider";
import { aiSettingsRequestRoutes } from "./ai-settings-requests/route";
import { conversionMonitorRoutes } from "./analytics/conversion-monitor/route";
import { forecastingDashboardRoutes } from "./analytics/forecasting-dashboard/route";
import { warehouseSlasRoutes } from "./analytics/warehouse-slas/route";
import { AuthContext, AuthProvider } from "./app/auth";
import { RedoAdminMarketingRpcClientProvider } from "./app/redo-admin-marketing-rpc-client-provider";
import {
  RedoAdminRpcClientContext,
  RedoAdminRpcClientProvider,
} from "./app/redo-admin-rpc-client-provider";
import { TanStackQueryDevtools } from "./app/tanstack-query-devtools";
import { UserProvider } from "./app/user";
import { askRedoRoutes } from "./ask-redo/route";
import { auditLogsRoutes } from "./audit-logs/route";
import { billingRoutes } from "./billing/route";
import { RedoAdminClient } from "./client";
import { RedoAdminClientContext } from "./client/context";
import { REDO_ADMIN_SERVER_URL } from "./config";
import { conversionRoutes } from "./conversion/route";
import { customReportsRoutes } from "./custom-reports/route";
import { globalSearchRoutes } from "./global-search/route";
import { goldenDomeRoutes } from "./golden-dome/route";
import { hooksRoutes } from "./hooks-overview/route";
import { incidentsRoutes } from "./incidents/incidents-routes";
import { loginRoutes } from "./login/route";
import { marketingRoutes } from "./marketing/route";
import { merchantEmailRoutes } from "./merchant-emails/route";
import { merchantSubmissionRoutes } from "./merchant-submissions/route";
import { rateCalculatorRoutes } from "./oms-pricing-tool/route";
import { onboardingsRoutes } from "./onboardings/route";
import { orderEditingRoutes } from "./order-editing/route";
import { partnersRoutes } from "./partners/route";
import { pmAgentRoutes } from "./pm-agent/route";
import { productFeedbackRoutes } from "./product-feedback/product-feedback-routes";
import { reclaimDisputesRoutes } from "./reclaim-disputes/route";
import { redoUsesRedoRoutes } from "./redo-uses-redo/route";
import { aiExchangeAnalyticsRoutes } from "./returns/ai-exchange-analytics/route";
import { integrationMetricsRoutes } from "./returns/integration-metrics/route";
import { marketRateCalculationRoutes } from "./returns/market-rate-calculation/route";
import { packageProtectionPlusRoutes } from "./returns/package-protection-plus/route";
import { temporalRoutes } from "./returns/temporal/route";
import { usefulFunctionsRoutes } from "./returns/useful-functions/route";
import { sendcloudContractRoutes } from "./sendcloud-contracts/route";
import { settingsAgentRoutes } from "./settings-agent/route";
import { shippingClaimRoutes } from "./shipping-claims/route";
import { slackMessagingRoutes } from "./slack-messaging/route";
import "./styles.css";
import { supportRoutes } from "./support/route";
import { surchargeDisputeRoutes } from "./surcharge-disputes/route";
import { teamRoutes } from "./team/route";
import { ticketValidationRoutes } from "./ticket-validation/route";
import { warehouseManagementRoutes } from "./warehouse-management/navigation/route";

const RouterRoot = memo(function RouterRoot() {
  const breadcrumbs = useBreadcrumbs();
  useDocumentTitle(
    breadcrumbs
      ? `${breadcrumbs
          .map((breadcrumb) => breadcrumb.name)
          .join(" ")} — Redo Admin`
      : "Redo Admin",
  );
  const teamMatch = useMatch("teams/:teamId/*");
  const auth = useContext(AuthContext);
  return (
    <>
      <ThursdayBootWarningBanner
        email={auth?.email}
        teamId={teamMatch?.params.teamId}
      />
      <Outlet />
    </>
  );
});

const router = createBrowserRouter([
  {
    path: "/",
    element: <RouterRoot />,
    errorElement: <NotFoundPage />,
    children: [
      { index: true, element: <Navigate to="login" /> },
      { path: "login", element: <Outlet />, children: loginRoutes },
      { path: "teams", element: <Outlet />, children: teamRoutes },
      { path: "agents", element: <Outlet />, children: agentRoutes },
      { path: "ask-redo", element: <Outlet />, children: askRedoRoutes },
      {
        path: "global-search",
        element: <Outlet />,
        children: globalSearchRoutes,
      },
      { path: "marketing", element: <Outlet />, children: marketingRoutes },
      { path: "conversion", element: <Outlet />, children: conversionRoutes },
      { path: "hooks", element: <Outlet />, children: hooksRoutes },
      {
        path: "order-editing",
        element: <Outlet />,
        children: orderEditingRoutes,
      },
      {
        path: "merchant-emails",
        element: <Outlet />,
        children: merchantEmailRoutes,
      },
      {
        path: "merchant-submissions",
        element: <Outlet />,
        children: merchantSubmissionRoutes,
      },
      { path: "onboardings", element: <Outlet />, children: onboardingsRoutes },
      { path: "partners", element: <Outlet />, children: partnersRoutes },
      { path: "incidents", element: <Outlet />, children: incidentsRoutes },
      {
        path: "product-feedback",
        element: <Outlet />,
        children: productFeedbackRoutes,
      },
      { path: "audit-logs", element: <Outlet />, children: auditLogsRoutes },
      { path: "billing", element: <Outlet />, children: billingRoutes },
      {
        path: "rate-calculator",
        element: <Outlet />,
        children: rateCalculatorRoutes,
      },
      {
        path: "ai-exchange-analytics",
        element: <Outlet />,
        children: aiExchangeAnalyticsRoutes,
      },
      {
        path: "redo-uses-redo",
        element: <Outlet />,
        children: redoUsesRedoRoutes,
      },
      {
        path: "slack-messaging",
        element: <Outlet />,
        children: slackMessagingRoutes,
      },
      { path: "golden-dome", element: <Outlet />, children: goldenDomeRoutes },
      { path: "support", element: <Outlet />, children: supportRoutes },
      {
        path: "carrier-claims",
        element: <Outlet />,
        children: shippingClaimRoutes,
      },
      {
        path: "sendcloud-contracts",
        element: <Outlet />,
        children: sendcloudContractRoutes,
      },
      {
        path: "surcharge-disputes",
        element: <Outlet />,
        children: surchargeDisputeRoutes,
      },
      { path: "temporal", element: <Outlet />, children: temporalRoutes },
      {
        path: "integration-metrics",
        element: <Outlet />,
        children: integrationMetricsRoutes,
      },
      {
        path: "market-rate-calculation",
        element: <Outlet />,
        children: marketRateCalculationRoutes,
      },
      {
        path: "useful-functions",
        element: <Outlet />,
        children: usefulFunctionsRoutes,
      },
      {
        path: "ai-settings-requests",
        element: <Outlet />,
        children: aiSettingsRequestRoutes,
      },
      {
        path: "settings-agent",
        element: <Outlet />,
        children: settingsAgentRoutes,
      },
      {
        path: "warehouse-management",
        element: <Outlet />,
        children: warehouseManagementRoutes,
      },
      { path: "analytics", element: <Outlet />, children: customReportsRoutes },
      {
        path: "analytics/conversion-monitor",
        element: <Outlet />,
        children: conversionMonitorRoutes,
      },
      {
        path: "analytics/forecasting-dashboard",
        element: <Outlet />,
        children: forecastingDashboardRoutes,
      },
      {
        path: "analytics/warehouse-slas",
        element: <Outlet />,
        children: warehouseSlasRoutes,
      },
      {
        path: "ticket-validation",
        element: <Outlet />,
        children: ticketValidationRoutes,
      },
      { path: "pm-agent", element: <Outlet />, children: pmAgentRoutes },
      { path: "aeo", element: <Outlet />, children: aeoDashboardRoutes },
      {
        path: "reclaim-disputes",
        element: <Outlet />,
        children: reclaimDisputesRoutes,
      },
      {
        path: "returns/package-protection-plus",
        element: <Outlet />,
        children: packageProtectionPlusRoutes,
      },
    ],
  },
]);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const AdminApp = () => {
  return composeWrappers(
    (inner) => (
      <QueryClientProvider client={queryClient}>
        {inner}
        <TanStackQueryDevtools />
      </QueryClientProvider>
    ),
    (inner) => <NotificationProvider>{inner}</NotificationProvider>,
    (inner) => <CustomSnackbarProvider>{inner}</CustomSnackbarProvider>,
    (inner) => <AuthProvider>{inner}</AuthProvider>,
    (inner) => <ThemeProvider>{inner}</ThemeProvider>,
    (inner) => (
      <AuthContext.Consumer>
        {(auth) => (
          <RedoAdminRpcClientProvider
            authToken={auth?.token}
            baseUrl={REDO_ADMIN_SERVER_URL}
          >
            <RedoAdminAgentsRpcClientProvider
              authToken={auth?.token}
              baseUrl={REDO_ADMIN_SERVER_URL}
            >
              <RedoAdminMarketingRpcClientProvider
                authToken={auth?.token}
                baseUrl={REDO_ADMIN_SERVER_URL}
              >
                <RedoAdminClientContext.Provider
                  value={
                    new RedoAdminClient(REDO_ADMIN_SERVER_URL, auth?.token)
                  }
                >
                  <MoneyWrapper>
                    <UserProvider>{inner}</UserProvider>
                  </MoneyWrapper>
                </RedoAdminClientContext.Provider>
              </RedoAdminMarketingRpcClientProvider>
            </RedoAdminAgentsRpcClientProvider>
          </RedoAdminRpcClientProvider>
        )}
      </AuthContext.Consumer>
    ),
  )(<RouterProvider router={router} />);
};

function MoneyWrapper({ children }: { children: React.ReactNode }) {
  const client = useContext(RedoAdminRpcClientContext);
  return (
    <CurrencyConverterProvider
      exchangeRates={async () =>
        new Map(
          Object.entries(await client!.getExchangeRates(null)) as [
            Currency,
            number,
          ][],
        )
      }
    >
      {children}
    </CurrencyConverterProvider>
  );
}

const root = createRoot(document.getElementById("root")!);

async function renderAdminApp() {
  if (globalThis.REDO_MOCK_MODE === "true") {
    const { initializeAdminMocks } = await import("./mock-bootstrap");
    await initializeAdminMocks();
  }
  root.render(<AdminApp />);
}

void renderAdminApp();
